import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AnimatePresence } from "motion/react";
import { useIntl } from "react-intl";
import { DoclistOutdatedDialog } from "~/features/chat/components/modals/DoclistOutdatedDialog";

import {
  getGetPageTextContentQueryKey,
  useGetPageTextContent,
} from "~/api/documents/documents";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { useChatDocumentParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import logger from "~/utils/logger";

import { usePageActions } from "./hooks/usePageActions";
import { usePageNavigation } from "./hooks/usePageNavigation";
import { usePageSelection } from "./hooks/usePageSelection";

import {
  DocViewerList,
  type DocViewerListHandle,
} from "./components/docViewerList";

import { GetDoclist200 } from "~/api/model";
import { PageItem } from "~/features/chat/hooks/useDocList";
import { PageNumberPanel } from "./components/PageNumberPanel";
import { SelectedPagePanel } from "./components/SelectedPagePanel";

type DocViewerProps = {
  pageItems: PageItem[];
  docList: GetDoclist200;
  isViewOnly: boolean;
};

export const DocViewer = ({
  pageItems,
  docList,
  isViewOnly,
}: DocViewerProps) => {
  const { organizationId, caseId, chatId } = useChatDocumentParams();
  const [searchParams] = useDocumentSearchParams();
  const { project } = useParentCase();
  const { toast } = useToast();
  const intl = useIntl();
  const queryClient = useQueryClient();

  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<DocViewerListHandle>(null);

  const [isDoclistOutdated, setIsDoclistOutdated] = useState(false);
  const [recognizeModalOpen, setRecognizeModalOpen] = useState(false);
  const [isZoomActive, setIsZoomActive] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [pageImageUrl, setPageImageUrl] = useState<string | undefined>(
    undefined
  );
  const [pageRotation, setPageRotation] = useState(0);

  const {
    selectedPage,
    selectedPageId,
    selectedPageNumber,
    togglePageSelection,
    clearSelectedPages,
  } = usePageSelection(pageItems);

  const totalPages = useMemo(() => {
    return pageItems.length;
  }, [pageItems]);

  const modalTitle = useMemo(() => {
    if (!selectedPage) return "Page Content Recognition";

    const section = docList?.tree
      .flatMap((doc) => doc.sections)
      .find((sec) => sec.id === selectedPage.sectionId);

    const sectionName = section?.name || "Unknown Section";

    return `${sectionName} – Page ${selectedPageNumber}`;
  }, [selectedPage, selectedPageNumber, docList]);

  const { currentPageNumber, setCurrentPageNumber } = usePageNavigation({
    pageItems,
  });

  const handleRotateSelectedPage = async () => {
    if (!selectedPage) return;

    try {
      await rotatePage();
    } catch (error) {
      // Handle any errors from the hook
    }
  };

  const handleDeleteSelectedPage = async () => {
    try {
      await deletePage();
      toast({
        title: intl.formatMessage({
          id: "docViewer.deletePage.success",
          defaultMessage: "Page deleted",
        }),
        description: intl.formatMessage({
          id: "docViewer.deletePage.successDescription",
          defaultMessage: "The page has been successfully removed.",
        }),
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setIsDoclistOutdated(true);
          return;
        }
      }
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "docViewer.deletePage.error",
          defaultMessage: "Failed to delete the page. Please try again.",
        }),
      });
    }
  };

  const { rotatePage, deletePage, isRotating } = usePageActions({
    organizationId,
    caseId,
    chatId,
    projectId: project?.id,
    selectedPage,
    docList,
    clearSelectedPages,
  });

  const {
    data: pageContentData,
    isLoading: isPageContentLoading,
    error: pageContentError,
  } = useGetPageTextContent(
    organizationId,
    project?.id || "",
    selectedPageId ?? "",
    {
      query: {
        enabled: !!selectedPageId,
      },
    }
  );

  const handleRecognizeSelectedPage = async () => {
    if (selectedPage) {
      setIsContentLoading(true);
      setRecognizeModalOpen(true);
      setPageRotation(selectedPage.rotate);

      try {
        const urls = await selectedPage.getUrls();
        setPageImageUrl(urls.scanUrl || urls.highResUrl || undefined);
      } catch (error) {
        setPageImageUrl(undefined);
      } finally {
        setIsContentLoading(false);
      }
    }
  };

  const handleToggleZoom = () => {
    setIsZoomActive(!isZoomActive);
  };

  const handleDoclistUpdate = () => {
    setIsDoclistOutdated(false);
  };

  const handleContentUpdate = useCallback(() => {
    if (selectedPageId && project?.id) {
      queryClient.invalidateQueries({
        queryKey: getGetPageTextContentQueryKey(
          organizationId,
          project.id,
          selectedPageId
        ),
      });
    }
  }, [queryClient, organizationId, project?.id, selectedPageId]);

  const isProgrammaticChange = useRef(false);

  const handleCurrentPageChange = useCallback(
    (index: number) => {
      if (currentPageNumber === index + 1) return;

      isProgrammaticChange.current = true;
      setCurrentPageNumber(index + 1);
    },
    [currentPageNumber, setCurrentPageNumber]
  );

  const handlePageChangeFromPanel = (page: number) => {
    setCurrentPageNumber(page);
  };

  useEffect(() => {
    if (listRef.current && !isProgrammaticChange.current) {
      listRef.current.scrollToIndex(currentPageNumber - 1);
    }
    isProgrammaticChange.current = false;
  }, [listRef, currentPageNumber]);

  useEffect(() => {
    setIsZoomActive(false);
  }, [selectedPageId, currentPageNumber]);

  useEffect(() => {
    if (!recognizeModalOpen) {
      setPageImageUrl(undefined);
      setPageRotation(0);
    }
  }, [recognizeModalOpen]);

  // Track if we should clear selection when the selected page goes off screen
  // This is triggered when URL navigation happens (e.g., clicking left sidebar)
  const shouldClearOnOffscreenRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      searchParams.pageId &&
      selectedPageId &&
      searchParams.pageId !== selectedPageId
    ) {
      // Don't clear immediately - wait until the selected page is off screen
      shouldClearOnOffscreenRef.current = selectedPageId;
    }
  }, [searchParams.pageId, selectedPageId]);

  const handleSelectedPageOffscreen = useCallback(() => {
    // Clear selection when the selected page goes completely off screen
    // This happens either due to URL navigation or user scrolling
    if (selectedPageId) {
      logger.info("DocViewer: Clearing selection - page went off screen", {
        selectedPageId,
      });
      clearSelectedPages();
      shouldClearOnOffscreenRef.current = null;
    }
  }, [selectedPageId, clearSelectedPages]);

  return (
    <div className="h-screen overflow-hidden relative">
      {isDoclistOutdated && (
        <DoclistOutdatedDialog
          open={isDoclistOutdated}
          setOpen={setIsDoclistOutdated}
          onCancel={() => setIsDoclistOutdated(false)}
          onUpdate={handleDoclistUpdate}
        />
      )}

      <div ref={containerRef} className="bg-gray-100 h-full relative">
        <DocViewerList
          ref={listRef}
          pages={pageItems}
          selectedPageId={selectedPageId}
          togglePageSelection={togglePageSelection}
          onScroll={handleCurrentPageChange}
          isZoomActive={isZoomActive}
          onSelectedPageOffscreen={handleSelectedPageOffscreen}
        />

        {/* Page Number Panel */}
        {totalPages > 1 && (
          <div className="absolute top-2 left-0 right-0 flex justify-center z-50">
            <PageNumberPanel
              currentPage={currentPageNumber}
              totalPages={totalPages}
              onPageChange={handlePageChangeFromPanel}
            />
          </div>
        )}

        {/* Selected Page Panel */}
        <AnimatePresence>
          {selectedPageId && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center z-50">
              <SelectedPagePanel
                isViewOnly={isViewOnly}
                unselectPages={clearSelectedPages}
                onRotateClicked={handleRotateSelectedPage}
                onDeleteClicked={handleDeleteSelectedPage}
                onRecognizeClicked={handleRecognizeSelectedPage}
                onZoomClicked={handleToggleZoom}
                selectedPageNumber={selectedPageNumber}
                isRotateLoading={isRotating}
                isRecognizeLoading={isPageContentLoading}
                isZoomActive={isZoomActive}
              />
            </div>
          )}
        </AnimatePresence>
      </div>

      {recognizeModalOpen && (
        <MarkdownContentModal
          title={modalTitle}
          content={
            isPageContentLoading || isContentLoading
              ? "Loading content..."
              : pageContentError
                ? "Error loading content. This page may not have processed content available yet."
                : pageContentData?.content ||
                  "No content available for this page."
          }
          filename={pageContentData?.filename}
          authorName={pageContentData?.authorName}
          uploadDate={pageContentData?.uploadDate}
          open={recognizeModalOpen}
          onOpenChange={setRecognizeModalOpen}
          copyEventName="app/doc_viewer/recognize_modal/copy_all.button/click"
          exportOpenEventName="app/doc_viewer/recognize_modal/export.button/click"
          exportEventName="app/doc_viewer/recognize_modal/export_menu_open/export_action.link/click"
          terms={[]}
          pageImageUrl={pageImageUrl}
          pageRotation={pageRotation}
          organizationId={organizationId}
          projectId={project?.id}
          pageId={selectedPageId ?? undefined}
          onContentUpdate={handleContentUpdate}
          isViewOnly={isViewOnly}
        />
      )}
    </div>
  );
};
